"use client";

import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  LockOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { Button } from "../../../assets/AntDesign/Button";
import { Input } from "../../../assets/AntDesign/Input";

import useSetNewPassword from "../../hooks/useSetNewPassword";
import { ROUTES } from "../../../routes/patientRoutes/routePaths";
import { useTheme } from "../../../places/context/ThemeContext";
import chooseImage from "../../../assets/icons/tswira.png";
import icon1 from "../../../assets/images/logomedilink.svg";

import "../SignupPatient/SignupPatient.css";
import "./SetNewPassword.css";
import "../AuthLayout.css";

// doit correspondre à la durée de transition CSS (0.5s) définie dans SignupPatient.css
const SWAP_DURATION = 500;
// clé localStorage partagée entre toutes les pages auth (patient/doctor)
const SWAP_STORAGE_KEY = "authHeroOnLeft";

const SetNewPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();

  const {
    password,
    confirmPassword,
    error,
    loading,
    setPassword,
    setConfirmPassword,
    handleReset,
    onKeyDown,
    goLogin,
  } = useSetNewPassword();

  useEffect(() => {
    if (theme !== "light") setTheme("light");
  }, [theme, setTheme]);

  // ── état persistant de la position des cartes (hero à gauche ou à droite) ──
  const initialHeroOnLeft = (() => {
    if (typeof location.state?.heroOnLeft === "boolean") {
      return location.state.heroOnLeft;
    }
    const stored =
      typeof window !== "undefined"
        ? localStorage.getItem(SWAP_STORAGE_KEY)
        : null;
    return stored === null ? true : stored === "true";
  })();

  const [heroOnLeft, setHeroOnLeft] = useState(initialHeroOnLeft);

  // ── refs + état pour l'animation du swap (form <-> hero) ──
  const heroRef = useRef(null);
  const formWrapperRef = useRef(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapStyle, setSwapStyle] = useState({ form: 0, hero: 0 });

  const handleContinueAsDoctor = () => {
    if (isSwapping) return;

    const formEl = formWrapperRef.current;
    const heroEl = heroRef.current;

    if (formEl && heroEl) {
      const formRect = formEl.getBoundingClientRect();
      const heroRect = heroEl.getBoundingClientRect();
      const formDeltaX = heroRect.left - formRect.left;
      const heroDeltaX = formRect.left - heroRect.left;
      setSwapStyle({ form: formDeltaX, hero: heroDeltaX });
    }

    setIsSwapping(true);

    const newHeroOnLeft = !heroOnLeft;

    setTimeout(() => {
      localStorage.setItem(SWAP_STORAGE_KEY, String(newHeroOnLeft));
      navigate("/LoginDoctor", { state: { heroOnLeft: newHeroOnLeft } });
    }, SWAP_DURATION);
  };

  return (
    <div className="signup-container login-page">
      <div
        className={`signup-background ${
          heroOnLeft ? "pos-hero-left" : "pos-form-left"
        }`}
      >
        {/* ── Carte formulaire ── */}
        <section
          className="signup-form-wrapper"
          ref={formWrapperRef}
          style={
            isSwapping
              ? { transform: `translateX(${swapStyle.form}px)`, opacity: 0.15 }
              : undefined
          }
        >
          <div className="signup-form" onKeyDown={onKeyDown}>
            <div className="signup-form-header">
              <h1 className="signup-form-title-main">
                {t("auth.setNewPasswordTitle")}
              </h1>
              <p className="signup-form-subtitle">
                {t("auth.setNewPasswordSubtitle")}
              </p>
            </div>

            <div className="signup-form-content">
              {error && (
                <div className="sp-error-banner" role="alert">
                  <ExclamationCircleFilled className="sp-error-banner__icon" />
                  <span>{error}</span>
                </div>
              )}

              <div className="signup-form-group">
                <label className="signup-form-label">
                  {t("auth.newPassword")}{" "}
                  <span className="signup-required">*</span>
                </label>
                <Input.Password
                  placeholder={t("common.passwordPlaceholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`signup-form-input${
                    error ? " signup-password-mismatch" : ""
                  }`}
                  prefix={<LockOutlined />}
                  autoComplete="new-password"
                  aria-invalid={!!error}
                />
              </div>

              <div className="signup-form-group">
                <label className="signup-form-label">
                  {t("auth.confirmPassword")}{" "}
                  <span className="signup-required">*</span>
                </label>
                <Input.Password
                  placeholder={t("common.passwordPlaceholder")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`signup-form-input${
                    error ? " signup-password-mismatch" : ""
                  }`}
                  prefix={<LockOutlined />}
                  autoComplete="new-password"
                  aria-invalid={!!error}
                />
                {error && (
                  <span className="signup-error-message">{error}</span>
                )}
              </div>

              <div className="signup-form-group">
                <Button
                  onClick={handleReset}
                  className="signup-create-account-btn"
                  loading={loading}
                  disabled={loading}
                >
                  {t("auth.resetPassword")} <ArrowRightOutlined />
                </Button>
              </div>

              <div className="signup-login-link-wrapper">
                <span className="signup-login-text">
                  <span
                    className="signup-login-link"
                    onClick={goLogin}
                    role="button"
                    tabIndex={0}
                  >
                    ← {t("auth.backToLogin")}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ── Hero ── */}
        <aside
          className="signup-hero"
          ref={heroRef}
          style={{
            backgroundImage: `url(${chooseImage})`,
            ...(isSwapping
              ? { transform: `translateX(${swapStyle.hero}px)`, opacity: 0.15 }
              : {}),
          }}
        >
          <div className="signup-hero-glow" aria-hidden="true" />

          <button
            type="button"
            className="signup-back-home"
            onClick={() => navigate(ROUTES.LANDING ?? "/")}
            aria-label={t("common.backToHome", "Back to home page")}
          >
            <ArrowLeftOutlined />
            <span>{t("common.backToHome", "Back to home page")}</span>
          </button>

          <div className="signup-hero-top-icon" aria-hidden="true">
            <img src={icon1} alt="decorative icon" />
          </div>

          <div className="signup-hero-inner">
            <h1 className="signup-hero-title">
              {t(
                "signup.heroPanelTitle",
                "Are you joining as a healthcare professional?"
              )}
            </h1>
            <p className="signup-hero-sub">
              {t(
                "signup.heroPanelText",
                "This registration is designed for patients. If you are a doctor and wish to manage your practice, schedule appointments, and connect with patients, please "
              )}
              <strong>
                {t("signup.heroPanelTextStrong", "create a doctor account.")}
              </strong>
            </p>
            <button
              type="button"
              className="signup-hero-cta"
              onClick={handleContinueAsDoctor}
            >
              {t("signup.continueAsDoctor", "Continue as a doctor")}
              <ArrowRightOutlined />
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default SetNewPassword;